import { Location } from '@angular/common';
import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup } from '@angular/forms';
import { Subscription, of } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { SelectionItem } from '../../../core/component/select/select.component';
import { ColumnDefinition } from '../../../core/model/column-definition';
import { ListViewRow } from '../../../core/model/list-view-row';
import { LocalDate } from '../../../core/model/local-date';
import { Currency } from '../../../core/model/money';
import { ComponentMessageDispatcher } from '../../../core/service/component-message-dispather.service';
import { ColumnDefinitionFactory } from '../../../core/service/factory/column-definition-factory.service';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { MessageService } from '../../../core/service/message.service';
import { Article } from '../../model/article';
import { RentalChangeKind, RentalChangesUnit, RentalChangesUnitToString } from '../../model/const/enums';
import { ArticleService } from '../../service/data/article.service';
import { FeeChangeService } from '../../service/data/fee-change.service';

@Component({
    selector: 'app-rental-changes',
    templateUrl: './rental-changes.component.html',
    styleUrls: ['./rental-changes.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class RentalChangesComponent implements OnDestroy, OnInit {
  constructor(
    private _articleData: ArticleService,
    private _cf: ColumnDefinitionFactory,
    private _fb: FormBuilder,
    private _fc: FormControlExFactory,
    private _feeChangeService: FeeChangeService,
    private _location: Location,
    private _messageDispatcher: ComponentMessageDispatcher,
    private _messageService: MessageService
  ) {
    this.fromDate = LocalDate.fromDate(LocalDate.fromDate(LocalDate.today().addMonths(1)).firstDayInMonth());
    this.missedPeriodFromDate = new LocalDate(LocalDate.today().year, 1, 1);
    this.missedPeriodToDate = LocalDate.fromDate(this.fromDate.clone().addDays(-1));
    this.retroInvoiceFromDate = this.fromDate.clone();
    this.retroInvoiceToDate = this.retroInvoiceFromDate.clone();
    this.retroInvoiceToDate = LocalDate.fromDate(this.retroInvoiceToDate.lastDayInMonth());

    this.form = this._fb.group({
      articles: this._fb.control([]),
      fromDate: this._fb.group({}),
      rentalObjects: this._fb.control([]),
      rentalChangeValue: this._fc.createMandatory('LEASE_AGREEMENT.RENTAL_CHANGES_VALUE', null),
      rentalChangeUnit: this._fc.createMandatory('LEASE_AGREEMENT.RENTAL_CHANGES_UNIT', RentalChangesUnit.Persentage),
      roundPeriodRent: this._fc.create('LEASE_AGREEMENT.RENTAL_CHANGES_ROUND_PERIOD_RENT', false),
      useRetroactiveRent: this._fc.create('LEASE_AGREEMENT.RENTAL_CHANGES_USE_RETRO', false),
      missedPeriodFromDate: this._fb.group({}),
      missedPeriodToDate: this._fb.group({}),
      retroInvoiceFromDate: this._fb.group({}),
      retroInvoiceToDate: this._fb.group({}),
      changeInvoiceText: this._fc.create('LEASE_AGREEMENT.RENTAL_CHANGES_USE_CHANGE_INVOICETEXT', true),
      newInvoiceText: this._fc.create('LEASE_AGREEMENT.RENTAL_CHANGES_NEW_INVOICETEXT', 'Retroaktiv hyra för januari'),
      changeRetroactiveArticle: this._fc.create('LEASE_AGREEMENT.RENTAL_CHANGES_USE_CHANGE_RETROACTIVE_ARTICLE', false),
      retroactiveArticle: this._fc.create('LEASE_AGREEMENT.RENTAL_CHANGES_RETROACTIVE_ARTICLE', null),
      adjustmentType: this._fc.create('LEASE_AGREEMENT.RENTAL_CHANGES_CHANGE_FOR', RentalChangeKind.RentalObjectAndLeaseAgreement),
    });
    this.articleColumnDefinitions = [
      this._cf.create('ARTICLE_LIST.CODE_COLUMN', 'code'),
      this._cf.create('ARTICLE_LIST.CAPTION_COLUMN', 'caption'),
    ];
    this.rentalChangeColumnDefinitions = [
      this._cf.create('LEASE_AGREEMENT.LEASE_AGREEMENT_NUMBER', 'leaseAgreementNumber'),
      this._cf.create('RENTAL_OBJECT_LIST.STREET_COLUMN', 'rentalObjectAddress'),
      this._cf.createMoney('LEASE_AGREEMENT.RENTAL_CHANGES_PREVIOUS_PERIOD_RENT', 'previousPeriodRent'),
      this._cf.createMoney('LEASE_AGREEMENT.RENTAL_CHANGES_NEW_PERIOD_RENT', 'newPeriodRent'),
      this._cf.createMoney('LEASE_AGREEMENT.RENTAL_CHANGES_PREVIOUS_YEAR_RENT', 'previousYearRent'),
      this._cf.createMoney('LEASE_AGREEMENT.RENTAL_CHANGES_NEW_YEAR_RENT', 'newYearRent'),
      this._cf.createMoney('LEASE_AGREEMENT.RENTAL_CHANGES_DIFFERENCE', 'yearRentDifference'),
    ];
    this.objectRentalChangeColumnDefinitions = [
      this._cf.create('RENTAL_OBJECT_LIST.RENTAL_ID_COLUMN', 'rentalId'),
      this._cf.create('RENTAL_OBJECT_LIST.STREET_COLUMN', 'rentalObjectAddress'),
      this._cf.createMoney('LEASE_AGREEMENT.RENTAL_CHANGES_PREVIOUS_PERIOD_RENT', 'previousPeriodRent'),
      this._cf.createMoney('LEASE_AGREEMENT.RENTAL_CHANGES_NEW_PERIOD_RENT', 'newPeriodRent'),
      this._cf.createMoney('LEASE_AGREEMENT.RENTAL_CHANGES_PREVIOUS_YEAR_RENT', 'previousYearRent'),
      this._cf.createMoney('LEASE_AGREEMENT.RENTAL_CHANGES_NEW_YEAR_RENT', 'newYearRent'),
      this._cf.createMoney('LEASE_AGREEMENT.RENTAL_CHANGES_DIFFERENCE', 'yearRentDifference'),
    ];
  }

  adjustmentSelection: SelectionItem[] = [
    new SelectionItem('CONST.RENTAL_CHANGES_TYPE_OBJECT', RentalChangeKind.RentalObject),
    new SelectionItem('CONST.RENTAL_CHANGES_TYPE_AGREEMENT', RentalChangeKind.LeaseAgreement),
    new SelectionItem('CONST.RENTAL_CHANGES_TYPE_BOTH', RentalChangeKind.RentalObjectAndLeaseAgreement),
  ];

  form: FormGroup;
  private _subscription: Subscription;
  articles: ListViewRow[];
  retroactiveArticles: Article[];
  articleColumnDefinitions: ColumnDefinition[];

  get canChange(): boolean {
    return (this.rentalChanges && this.rentalChanges.length > 0) || (this.rentalObjectChanges && this.rentalObjectChanges.length > 0);
  }
  fromDate: LocalDate;
  missedPeriodFromDate: LocalDate;
  missedPeriodToDate: LocalDate;
  retroInvoiceFromDate: LocalDate;
  retroInvoiceToDate: LocalDate;

  get rentalChangeValue(): AbstractControl {
    return this.form.controls['rentalChangeValue'];
  }
  get rentalChangeUnit(): AbstractControl {
    return this.form.controls['rentalChangeUnit'];
  }
  get useRetroactiveRent(): AbstractControl {
    return this.form.controls['useRetroactiveRent'];
  }
  get changeInvoiceText(): AbstractControl {
    return this.form.controls['changeInvoiceText'];
  }
  get newInvoiceText(): AbstractControl {
    return this.form.controls['newInvoiceText'];
  }
  get retroactiveArticle(): AbstractControl {
    return this.form.controls['retroactiveArticle'];
  }
  get changeRetroactiveArticle(): AbstractControl {
    return this.form.controls['changeRetroactiveArticle'];
  }

  rentalChangeUnits = [
    { name: RentalChangesUnitToString(RentalChangesUnit.Persentage), value: RentalChangesUnit.Persentage },
    { name: RentalChangesUnitToString(RentalChangesUnit.PerSquareMeter), value: RentalChangesUnit.PerSquareMeter },
    { name: RentalChangesUnitToString(RentalChangesUnit.PerYear), value: RentalChangesUnit.PerYear },
  ];
  get roundPeriodRent(): AbstractControl {
    return this.form.controls['roundPeriodRent'];
  }
  isPreview: boolean;
  rentalChanges: any[];
  rentalObjectChanges: any[];
  rentalChangeColumnDefinitions: ColumnDefinition[];
  objectRentalChangeColumnDefinitions: ColumnDefinition[];

  cancel() {
    this.rentalChanges = [];
    this.isPreview = false;
  }

  ngOnDestroy() {
    if (this._subscription) {
      this._subscription.unsubscribe();
      this._subscription = null;
    }

    if (this._previewSub) {
      this._previewSub.unsubscribe();
    }
  }

  ngOnInit() {
    this._subscription = this._messageDispatcher.messages
      .pipe(
        switchMap(message => {
          this._messageDispatcher.messageDelivered();
          this.form.controls['rentalObjects'].setValue(message.rentalObjects);
          return this._feeChangeService.getRentalChangesRoundPeriodRentDefault(message.rentalObjects).pipe(
            tap(roundPeriodRent => this.roundPeriodRent.setValue(roundPeriodRent)),
            switchMap(() => this._articleData.getForRentalChanges(message.category)),
            tap(data => {
              this.articles = data.map(article => new ListViewRow(article));
            }),
            switchMap(() => of(message.category))
          );
        }),
        switchMap(category => {
          return this._articleData.getByCategory(category, Currency.SEK);
        }),
        catchError(error => {
          this._messageService.showError(error);
          throw error;
        })
      )
      .subscribe(articles => {
        this.retroactiveArticles = articles;
      });
  }

  previewObjects: boolean = true;
  previewLeases: boolean = true;

  private _previewSub: Subscription;
  preview() {
    if (this.form.invalid) return;

    this.form.controls['articles'].setValue(this.articles.filter(x => x.selected).map(x => x.dataSource.id));

    this._feeChangeService.previewRentalChanges(this.getRequestPayload()).subscribe(
      data => {
        this.previewLeases = this.form.controls['adjustmentType'].value !== RentalChangeKind.RentalObject;
        this.previewObjects = this.form.controls['adjustmentType'].value !== RentalChangeKind.LeaseAgreement;
        this.isPreview = true;
        this.rentalChanges = data.leaseAgreementChanges;
        this.rentalObjectChanges = data.rentalObjectChanges;
      },
      error => this._messageService.showError(error)
    );
  }

  working: boolean;

  save() {
    if (this.working) return;

    this.working = true;

    this._feeChangeService.changeRental(this.getRequestPayload()).subscribe(
      () => this._location.back(),
      error => {
        this.working = false;
        this._messageService.showError(error);
      }
    );
  }

  private getRequestPayload(): any {
    const formValue = this.form.value;
    const selectedRetroactiveArticle = formValue.retroactiveArticle;

    return {
      ...formValue,
      retroactiveArticle:
        typeof selectedRetroactiveArticle === 'string'
          ? selectedRetroactiveArticle
          : selectedRetroactiveArticle?.id ?? null,
    };
  }
}
